import {createSelector} from 'reselect'
import {AppState} from 'redux/config'
import {select_router_params} from 'redux/router/selectors'
import {
  getSelectorSettingsForCategory,
  getFilterOptions,
  getSelectorDatabaseOptions,
  getSelectorJobRequestCategoryOptions,
  getSelectorDataLoadTopicOptions,
  getSelectorStudyOptions,
  getSelectorDataVerificationHashObjectOptions,
  getSelectValue,
  getSelectTopicValue,
  getSelectorStudyEnvironmentOptions,
} from 'redux/jobRequests/helpers'

export const select_jobRequestOptions = (state: AppState) => state.jobRequests.jobRequestOptions
export const select_dataVerificationHashObjectOptions = (state: AppState) => state.jobRequests.dataVerificationHashObjectOptions
export const select_dataVerificationHashObject = (state: AppState) => state.jobRequests.dataVerificationHashObject
export const select_jobRequestCategory = (state: AppState) => state.jobRequests.jobRequestCategory
export const select_dataLoadTopic = (state: AppState) => state.jobRequests.dataLoadTopic
export const select_databaseOptions = (state: AppState) => state.jobRequests.database.databaseOptions
export const select_databaseId = (state: AppState) => state.jobRequests.database.databaseId
export const select_databaseName = (state: AppState) => state.jobRequests.database.hostname
export const select_studyOptions = (state: AppState) => state.jobRequests.study.studyOptions
export const select_studyUuid = (state: AppState) => state.jobRequests.study.studyUuid
export const select_studyName = (state: AppState) => state.jobRequests.study.studyName
export const select_studyEnvironmentOptions = (state: AppState) => state.jobRequests.studyEnvironment.studyEnvironmentOptions
export const select_studyId = (state: AppState) => state.jobRequests.studyEnvironment.studyId
export const select_studyEnvironmentUuid = (state: AppState) => state.jobRequests.studyEnvironment.studyEnvironmentUuid
export const select_studyEnvironmentName = (state: AppState) => state.jobRequests.studyEnvironment.studyEnvironmentName
export const select_objectId = (state: AppState) => state.jobRequests.objectId
export const select_changesAfterDate = (state: AppState) => state.jobRequests.changesAfterDate
export const select_changesAfterTime = (state: AppState) => state.jobRequests.changesAfterTime
export const select_jobRequestsHistory = (state: AppState) => state.jobRequests.jobRequestsHistory
export const select_gridSession = (state: AppState) => state.jobRequests.gridSession
export const select_gridSession_pageCount = (state: AppState) => state.jobRequests.gridSession.pageCount
export const select_gridSession_pageSizeOptions = (state: AppState) => state.jobRequests.gridSession.pageSizeOptions
export const select_status = (state: AppState) => state.jobRequests.status
export const select_error = (state: AppState) => state.jobRequests.error
export const select_statusDetails = (state: AppState) => state.jobRequests.statusDetails
export const select_requestIdDetails = (state: AppState) => state.jobRequests.requestIdDetails
export const select_gridSession_show_filters = (state: AppState) => state.jobRequests.gridSession.showFilters
export const select_gridSession_show_JobCreateRequestForm = (state: AppState) => state.jobRequests.gridSession.showJobCreateRequestForm

export const select_jobCreateRequest = (state: AppState) => state.jobRequests.dataLoadRequestDetails
export const select_showDetails = (state: AppState) => state.jobRequests.showDetails

export const select_specifiedUrls = (state: AppState) => state.jobRequests.specifiedUrls
export const select_urlSelectionType = (state: AppState) => state.jobRequests.urlSelectionType

export const select_changesAfter_timeDateUTC = createSelector(
  select_changesAfterDate,
  select_changesAfterTime,
  (date, time) => {
    if (!date) return null
    const dateValue: string = date.slice(0, date.indexOf('T'))
    const timeValue: string = time ?  time.slice(time.indexOf('T')) : 'T00:00:00.000Z'
    return dateValue + timeValue
  }
)

export const select_filtersVM = createSelector(
  select_router_params,
  select_jobRequestOptions,
  ({category}, jobRequestOptions) => {
    const filterOptions = getFilterOptions(jobRequestOptions)
    return {
      category: {value: category, label: filterOptions.find(o => o.value === category)?.label || ''},
    }
  }
)

export const select_jobRequestFormVM = createSelector(
  select_jobRequestOptions,
  select_jobRequestCategory,
  select_dataLoadTopic,
  select_databaseOptions,
  select_databaseId,
  select_databaseName,
  select_studyOptions,
  select_studyUuid,
  select_studyName,
  select_changesAfterDate,
  select_changesAfterTime,
  select_dataVerificationHashObjectOptions,
  select_dataVerificationHashObject,
  select_objectId,
  select_studyEnvironmentOptions,
  select_studyId,
  select_studyEnvironmentUuid,
  select_studyEnvironmentName,
  (
    jobRequestOptions,
    jobRequestCategory,
    dataLoadTopic,
    databaseOptions,
    databaseId,
    databaseName,
    studyOptions,
    studyUuid,
    studyName,
    dateIsChosen,
    timeIsChosen,
    dataVerificationHashObjectOptions,
    dataVerificationHashObject,
    objectId,
    studyEnvironmentOptions,
    studyId,
    studyEnvironmentUuid,
    studyEnvironmentName
  ) => {
    const categorySelectorSettings = getSelectorSettingsForCategory(jobRequestCategory, jobRequestOptions)
    const enableKafkaTopic = categorySelectorSettings?.enableKafkaTopic ?? false
    const enableDataVerificationHashObject = categorySelectorSettings?.enableDataVerificationHashObject ?? false
    const enableStudy = categorySelectorSettings?.enableStudy ?? false
    const isStudyRequired = categorySelectorSettings?.isStudyRequired ?? false
    const enableChangesAfterDate = categorySelectorSettings?.enableChangesAfterDate ?? false
    const enableObjectId = categorySelectorSettings?.enableObjectId ?? false
    const enableStudyEnvironment = categorySelectorSettings?.enableStudyEnvironment ?? false

    const jobRequestCategoryOption = getSelectValue(jobRequestCategory.value, jobRequestCategory.label)

    const jobRequestCategorySelectorOptions = getSelectorJobRequestCategoryOptions(jobRequestOptions)
    const dataLoadTopicSelectorOptions = getSelectorDataLoadTopicOptions(categorySelectorSettings)
    const dataLoadTopicOption = getSelectTopicValue(dataLoadTopic?.value, dataLoadTopic?.label)
    const databaseSelectorValues = getSelectorDatabaseOptions(databaseOptions, categorySelectorSettings?.enableMultipleUrlSelection ?? false)
    const selectStudyOptions = getSelectorStudyOptions(studyOptions)
    const selectStudyEnvironmentsOptions = getSelectorStudyEnvironmentOptions(studyEnvironmentOptions)
    const dataVerificationHashObjectSelectorOptions = getSelectorDataVerificationHashObjectOptions(dataVerificationHashObjectOptions)
    const dataVerificationHashObjectOption = getSelectValue(dataVerificationHashObject ?? '', dataVerificationHashObject?.replace(/_/g, ' ') ?? '')
    const validObjectId = (objectId ?? '').trim().length > 0
    const requiredObjectId = enableDataVerificationHashObject
    const requiredObjectIdText = enableDataVerificationHashObject ? 'Enter Bucket Id' : ''
    const jobRequestButtonDisabled = (!(
      jobRequestCategory?.value 
      && (!enableKafkaTopic || (enableKafkaTopic && dataLoadTopic?.value)) 
      && (!enableDataVerificationHashObject || (enableDataVerificationHashObject && dataVerificationHashObject && validObjectId))
      && (isStudyRequired ? studyUuid : true)
      && (!enableStudyEnvironment || (enableStudyEnvironment && studyEnvironmentUuid))
      && databaseId))
    
    return {
      jobRequestCategory: jobRequestCategoryOption,
      dataLoadTopic: dataLoadTopicOption,
      jobRequestOptions,
      databaseId,
      databaseName,
      studyUuid,
      studyName,
      dateIsChosen,
      timeIsChosen,
      enableKafkaTopic,
      enableDataVerificationHashObject,
      enableStudy,
      isStudyRequired,
      enableChangesAfterDate,
      enableObjectId,
      enableStudyEnvironment,
      jobRequestCategorySelectorOptions,
      dataLoadTopicSelectorOptions,
      databaseSelectorValues,
      selectStudyOptions,
      dataVerificationHashObjectSelectorOptions,
      dataVerificationHashObject: dataVerificationHashObjectOption,
      requiredObjectId,
      requiredObjectIdText,
      jobRequestButtonDisabled,
      selectStudyEnvironmentsOptions,
      studyId,
      studyEnvironmentName,
      studyEnvironmentUuid,
      defaultMenuIsOpen: false, // in testing it will be set to true, to work around onex Selector component's testing difficulties
    }
  }
)
